"use client";
import { useEffect, useRef, useState, type ReactNode } from "react";

type Direction = "up" | "left" | "right" | "none";

interface FadeInOnScrollProps {
  children: ReactNode;
  direction?: Direction;
  delay?: number;
  className?: string;
  /** Once visible, stay visible (default true). */
  once?: boolean;
}

/**
 * Lightweight client-side wrapper that fades its children in when scrolled into view.
 * Children render as plain DOM (server-rendered HTML), so all SEO-relevant content
 * remains crawlable — this wrapper only toggles CSS classes.
 */
export const FadeInOnScroll = ({
  children,
  direction = "up",
  delay = 0,
  className = "",
  once = true,
}: FadeInOnScrollProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect reduced-motion preference: show immediately, no animation.
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once]);

  const hiddenTransform =
    direction === "left"
      ? "-translate-x-8"
      : direction === "right"
        ? "translate-x-8"
        : direction === "up"
          ? "translate-y-6"
          : "";

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={[
        "transition-all duration-700 ease-out will-change-transform",
        visible
          ? "opacity-100 translate-x-0 translate-y-0"
          : `opacity-0 ${hiddenTransform}`,
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
};
